import difference from 'lodash/difference';
import pick from 'lodash/pick';
import type { Dispatch, SetStateAction } from 'react';

import type { CanvasDebugger } from 'flow-builder/debug/debugger';
import type { CanvasComponent } from 'flow-builder/types/canvasComponent';

interface BaseRendererCallbacks<
  TSourceItem,
  TComponent extends CanvasComponent<TComponent['props']>,
> {
  onGetProps: (item: TSourceItem) => TComponent['props'];
  onGetItem: (props: TComponent['props']) => TComponent;
  onDestroyItems: (ids: string[]) => void;
  onAddItems: (items: Record<string, TComponent>) => void;
  onUpdateState: Dispatch<SetStateAction<Record<string, TComponent>>>;
  onItemRedraw?: (items: Record<string, TComponent>) => void;
}

interface BaseRendererProps<
  TRendererState,
  TRendererSourceItem,
  TComponent extends CanvasComponent<TComponent['props']>,
> extends BaseRendererCallbacks<TRendererSourceItem, TComponent> {
  state: TRendererState;
  source: Record<string, TRendererSourceItem>;
  debug?: {
    name: string;
    canvasDebugger: CanvasDebugger;
  };
}

export const componentBaseRenderer = <
  TRendererSourceItem extends { id: string },
  TComponent extends CanvasComponent<TComponent['props']>,
>(
  props: BaseRendererProps<
    Record<string, TComponent>,
    TRendererSourceItem,
    TComponent
  >,
): void => {
  const {
    source,
    state,
    debug,
    onGetProps,
    onGetItem,
    onDestroyItems,
    onAddItems,
    onUpdateState,
    onItemRedraw,
  } = props;

  const {
    isNewItemAdded,
    isItemUpdated,
    items: newItems,
    updated: updatedItems,
  } = Object.values(source).reduce<{
    isNewItemAdded: boolean;
    isItemUpdated: boolean;
    items: Record<string, TComponent>;
    updated: Record<string, TComponent>;
  }>(
    (acc, item) => {
      const { id } = item;
      const isItemCreated = !!state[id];

      const props: TComponent['props'] = onGetProps(item);

      if (!isItemCreated) {
        acc.isNewItemAdded = true;
        acc.items[id] = onGetItem(props);
      } else {
        const item = state[id];
        const isChanged = item.isChanged(props);

        if (isChanged) {
          item.redraw(props);

          acc.isItemUpdated = true;
          acc.updated[id] = item;
        } else {
          item.updatePropsWithoutRedraw(props);
        }
      }

      return acc;
    },
    {
      isNewItemAdded: false,
      isItemUpdated: false,
      items: {},
      updated: {},
    },
  );

  const itemIds = Object.keys(state);
  const sourceItemIds = Object.keys(source);

  const removedItemIds = difference(itemIds, sourceItemIds);
  const isItemRemoved = removedItemIds.length > 0;

  if (debug) {
    const { name, canvasDebugger } = debug;

    if (isNewItemAdded) {
      const addedItemIds = Object.keys(newItems);

      canvasDebugger?.debug(
        'Component Renderer',
        `Added ${addedItemIds.length} ${name} items`,
        addedItemIds,
      );
    }

    if (isItemUpdated) {
      const updatedItemIds = Object.keys(updatedItems);

      canvasDebugger?.debug(
        'Component Renderer',
        `Updated ${updatedItemIds.length} ${name} items`,
        updatedItemIds,
      );
    }

    if (isItemRemoved) {
      canvasDebugger?.debug(
        'Component Renderer',
        `Removed ${removedItemIds.length} ${name} items`,
        removedItemIds,
      );
    }
  }

  if (isItemUpdated) {
    onItemRedraw?.(updatedItems);
  }

  if (isItemRemoved) {
    onDestroyItems(removedItemIds);
  }

  if (isNewItemAdded) {
    onAddItems(newItems);
  }

  if (isNewItemAdded || isItemRemoved) {
    onUpdateState((items) => ({
      /*
       * Remove keys which are not in source items.
       */
      ...pick(items, sourceItemIds),
      ...newItems,
    }));
  }
};
